import { Dropdown, type MenuElement, MenuItem } from "prosemirror-menu";
import type { MarkType } from "prosemirror-model";
import { type Command, EditorState, NodeSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { isColorMark, schema } from "../adapter/schema";
import { ICON_SIZE } from "../icons";
import { FORMULA_ERROR_COLOR } from "../node-views/formula";
import {
  getSelectionColor,
  isFormulaSelected,
  isImageSelected,
  isSelectionInCodeBlock,
} from "../utils";

import { COLOR_PALETTE } from "./color-palette";

import { t } from "i18n/i18n";
import { TEXT_COLOR_ICON_PATHS } from "mds/utils/icon-paths";

const DEFAULT_TEXT_COLOR = "#000000";
const DEFAULT_CODE_BLOCK_TEXT_COLOR = "#FFFFFF";
const SVG_NS = "http://www.w3.org/2000/svg";

/** Returns true if the selected formula has a KaTeX render error; when true, FORMULA_ERROR_COLOR cannot be overridden. */
function formulaHasRenderError(view: EditorView): boolean {
  if (!isFormulaSelected(view.state)) return false;
  const sel = view.state.selection;
  const dom = view.nodeDOM(sel.from);
  if (!dom || !(dom instanceof HTMLElement)) return false;
  return dom.querySelector(".katex-error") !== null;
}

/** Returns the active text color for the icon indicator; defaults to black or white depending on context. */
function getDisplayedTextColor(view: EditorView): string {
  const { state } = view;
  const defaultColor = isSelectionInCodeBlock(state)
    ? DEFAULT_CODE_BLOCK_TEXT_COLOR
    : DEFAULT_TEXT_COLOR;

  if (formulaHasRenderError(view)) return FORMULA_ERROR_COLOR;

  const colorMarkType = schema.marks.color;
  if (!colorMarkType) return defaultColor;

  const sel = state.selection;

  if (sel instanceof NodeSelection) {
    const colorMark = sel.node.marks.find(isColorMark);
    return colorMark ? colorMark.attrs.color : defaultColor;
  }

  // Empty selection (cursor): use storedMarks or $from.marks() to reflect what the next typed character will look like.
  if (sel.empty) {
    const marks = state.storedMarks || sel.$from.marks();
    const colorMark = marks.find(isColorMark);
    return colorMark ? colorMark.attrs.color : defaultColor;
  }

  return getSelectionColor(state, sel.from, sel.to) ?? defaultColor;
}

/**
 * Command to apply a text color mark.
 * @param color - 6-digit hex color string; the schema validates and rejects other formats
 */
function applyTextColor(colorMarkType: MarkType, color: string): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    const attrs = { color };

    // If no text is selected, set the color for future input (stored mark); otherwise apply to selection
    const tr = empty
      ? state.tr.addStoredMark(colorMarkType.create(attrs))
      : state.tr.addMark(from, to, colorMarkType.create(attrs));
    if (dispatch) dispatch(tr);
    return true;
  };
}

/** Creates a custom dropdown that updates its icon color based on the current text color. */
export class DynamicColorPicker implements MenuElement {
  private dropdown: Dropdown;

  private barPathEl: SVGPathElement | null = null;

  constructor() {
    const colorSwatches: MenuElement[] = COLOR_PALETTE.map((color) => {
      return new MenuItem({
        label: "",
        render() {
          const swatch = document.createElement("div");
          swatch.className = "ProseMirror-color-swatch";
          swatch.setAttribute("data-color", color);
          swatch.style.backgroundColor = color;
          return swatch;
        },
        run: (state, dispatch) => {
          const colorMarkType = schema.marks.color;
          if (colorMarkType) {
            return applyTextColor(colorMarkType, color)(state, dispatch);
          }
          return false;
        },
      });
    });

    this.dropdown = new Dropdown(colorSwatches, {
      label: "",
      title: t("worksheet.rich_text_editor.text_color"),
    });
  }

  render(view: EditorView) {
    const { dom, update } = this.dropdown.render(view);

    // Set up the dropdown label
    const dropdownBtn = dom.querySelector(".ProseMirror-menu-dropdown") as HTMLElement;
    if (dropdownBtn) {
      const dropdownLabel = document.createElement("div");
      dropdownLabel.className = "color-picker-dropdown-label";

      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("viewBox", `0 0 ${ICON_SIZE} ${ICON_SIZE}`);
      svg.setAttribute("width", String(ICON_SIZE));
      svg.setAttribute("height", String(ICON_SIZE));

      const aPath = document.createElementNS(SVG_NS, "path");
      aPath.setAttribute("d", TEXT_COLOR_ICON_PATHS[0]);
      aPath.setAttribute("fill", "currentColor");

      const barPath = document.createElementNS(SVG_NS, "path");
      barPath.setAttribute("d", TEXT_COLOR_ICON_PATHS[1]);
      barPath.setAttribute("fill", getDisplayedTextColor(view));

      svg.appendChild(aPath);
      svg.appendChild(barPath);
      dropdownLabel.appendChild(svg);
      dropdownBtn.appendChild(dropdownLabel);
      this.barPathEl = barPath;
    }

    // Wrap the update function in order to update icon color when state changes
    const wrappedUpdate = (state: EditorState) => {
      const result = update(state);
      if (this.barPathEl) {
        this.barPathEl.setAttribute("fill", getDisplayedTextColor(view));
      }
      const disabled =
        isSelectionInCodeBlock(state) || formulaHasRenderError(view) || isImageSelected(state);
      dom.classList.toggle("ProseMirror-menu-disabled", disabled);
      return result;
    };

    return { dom, update: wrappedUpdate, focusable: dropdownBtn };
  }
}
